// The per-copy capability of ONE (user, collection) pair (plan 2026-10-16,
// C2): unknown | per_copy | name_level | rebuilding | unavailable.
//
// Every per-copy response feeds it (collectionCopies.ts reports each outcome
// here). Only a PROBE can raise a pair to per_copy; any other success merely
// confirms an `unknown` pair. A downgrade is immediate and sticky until a
// fresh probe answers 200: the UI hides per-copy surfaces, cancels queries and
// refuses mutations from the moment a 404 arrives on ANY per-copy call.
//
// Module state (one store for the app, like useSupabaseToken), read through
// useSyncExternalStore by useCopiesMode.
import { CAPABILITY, KEY_SEPARATOR, type Capability } from '../api/collectionCopies.codes'
import * as identityStore from '../hooks/useSupabaseToken'
import { collectionOwnerOf } from './collectionOwner'
import { forgetPerCopy, keepOnlyOwnerHint, rememberPerCopy } from './copiesModeHint'
import { clearCopyLineEdits } from './copyLineEdits'

export interface CapabilityEntry {
  state: Capability
  /** Date.now() of the last downgrade (re-probe 5 minutes after it). */
  downgradedAt: number | null
  /** The downgrade came from a 403 (another account owns the collection). */
  forbidden: boolean
  /** Bumped on every change; consumers compare it to drop stale work. */
  generation: number
}

const UNKNOWN: CapabilityEntry = { state: CAPABILITY.unknown, downgradedAt: null, forbidden: false, generation: 0 }

const entries = new Map<string, CapabilityEntry>()
const listeners = new Set<() => void>()

export function capabilityKey(userId: string, collectionId: string): string {
  return userId + KEY_SEPARATOR + collectionId
}

function notify() {
  for (const l of listeners) l()
}

export function getCapability(userId: string | null, collectionId: string | null): CapabilityEntry {
  if (!userId || !collectionId) return UNKNOWN
  return entries.get(capabilityKey(userId, collectionId)) ?? UNKNOWN
}

// --- identity: a verdict never outlives the owner it was made for -----------
//
// A forbidden or downgraded pair from account A must not follow the browser
// into B's session, nor into A's NEXT session (sign out, sign back in): the
// next session re-probes. Every owner change (sign-out, account switch, an
// anonymous session) drops every entry that is not the new owner's.

let watching = false

function watchIdentity(): void {
  if (watching) return
  watching = true
  try {
    const subscribe = identityStore.subscribeSupabaseIdentity
    if (typeof subscribe !== 'function') return
    let lastOwner: string | null | undefined = undefined
    subscribe(() => {
      let owner: string | null | undefined
      try {
        owner = collectionOwnerOf(identityStore.getSupabaseIdentity())
      } catch {
        return
      }
      if (owner === undefined || owner === lastOwner) return
      lastOwner = owner
      // The device's remembered per-copy answer follows the same rule, and
      // no queued change's state (baseline, pending edits) of the previous
      // owner survives into this session.
      keepOnlyOwnerHint(owner)
      clearCopyLineEdits()
      let changed = false
      for (const key of [...entries.keys()]) {
        if (userOfKey(key) !== owner) changed = forget(key) || changed
      }
      if (changed) notify()
    })
  } catch {
    // No identity store (a test double without it): explicit clears still apply.
  }
}

/** Forget every pair of `userId` (sign-out, account deletion). */
export function clearCapabilityForUser(userId: string): void {
  forgetPerCopy(userId)
  clearCopyLineEdits()
  let changed = false
  for (const key of [...entries.keys()]) {
    if (userOfKey(key) === userId) changed = forget(key) || changed
  }
  if (changed) notify()
}

function userOfKey(key: string): string {
  return key.slice(0, key.indexOf(KEY_SEPARATOR))
}

// Back to unknown, with a NEW generation (not deleted): a probe or a write
// that started under the old verdict must still see the pair change, so it
// can never promote a pair it was not issued for.
function forget(key: string): boolean {
  const prev = entries.get(key)
  if (!prev || (prev.state === CAPABILITY.unknown && !prev.forbidden && prev.downgradedAt === null)) return false
  entries.set(key, { ...UNKNOWN, generation: prev.generation + 1 })
  return true
}

function set(userId: string, collectionId: string, next: Omit<CapabilityEntry, 'generation'>, force = false) {
  watchIdentity()
  const key = capabilityKey(userId, collectionId)
  const prev = entries.get(key) ?? UNKNOWN
  if (!force && prev.state === next.state && prev.forbidden === next.forbidden && prev.downgradedAt === next.downgradedAt) return
  entries.set(key, { ...next, generation: prev.generation + 1 })
  notify()
}

/** A probe answered 200: the pair is per_copy. */
export function markPerCopy(userId: string, collectionId: string): void {
  rememberPerCopy(userId, collectionId)
  set(userId, collectionId, { state: CAPABILITY.perCopy, downgradedAt: null, forbidden: false })
}

/**
 * A probe answered 200, but it may have started BEFORE a downgrade landed:
 * promote only when the pair's generation is still the one captured when the
 * probe started. A stale probe never re-promotes a downgraded pair.
 */
export function markPerCopyIfUnchanged(userId: string, collectionId: string, generationAtStart: number): boolean {
  if (getCapability(userId, collectionId).generation !== generationAtStart) return false
  markPerCopy(userId, collectionId)
  return true
}

/** The state is a downgrade (the per-copy surfaces are off). */
export function isDowngraded(state: Capability): state is Exclude<Capability, 'per_copy' | 'unknown'> {
  return state !== CAPABILITY.perCopy && state !== CAPABILITY.unknown
}

/**
 * A non-probe per-copy call succeeded. It confirms an unknown pair; it never
 * lifts a downgrade (a response from a request that started before the 404
 * must not undo it).
 */
export function confirmPerCopy(userId: string, collectionId: string): void {
  if (getCapability(userId, collectionId).state !== CAPABILITY.unknown) return
  markPerCopy(userId, collectionId)
}

/** A response that maps to a downgrade (C1). */
export function downgrade(
  userId: string, collectionId: string, state: Exclude<Capability, 'per_copy' | 'unknown'>, opts: { forbidden?: boolean } = {},
): void {
  // Always a new generation, even when the state repeats: every downgrade
  // must invalidate a probe or a write that started before it.
  // A definitive answer (name_level, rebuilding) also drops the device's
  // remembered per-copy answer; `unavailable` (an outage, a session that
  // could not be used) says nothing about the server's mode and keeps it.
  if (state !== CAPABILITY.unavailable) forgetPerCopy(userId, collectionId)
  set(userId, collectionId, { state, downgradedAt: Date.now(), forbidden: !!opts.forbidden }, true)
}

export function subscribeCapability(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

/**
 * True when (user, collection) is per_copy but the stored snapshot's proxy
 * set was not certified for it: unknown proxy data (no proxy_names on some
 * page) that is not explained by a diverged row, or names synced for another
 * user. Such a snapshot is replaced by a full walk even on a matching version.
 */
export function needsProxyWalk(
  userId: string, collectionId: string,
  stored: { proxyNamesKnown: boolean; copiesUnavailable: boolean; ownedCardsUserId: string | null },
): boolean {
  if (getCapability(userId, collectionId).state !== CAPABILITY.perCopy) return false
  if (stored.ownedCardsUserId !== userId) return true
  return !stored.proxyNamesKnown && !stored.copiesUnavailable
}

/** Test seam. */
export function __resetCopiesCapability(): void {
  entries.clear()
  notify()
}
